<script setup lang="ts">
import { AppLink } from '#components'
import { computed } from 'vue'

import type { BannerTypeFragment } from '#graphql-operations'

const props = withDefaults(defineProps<{
  banner: BannerTypeFragment
  lazy: boolean
  bannerCount?: number
  fullWidth?: boolean
}>(), {
  bannerCount: 1,
  fullWidth: false,
})

// const banner = reactive({
//   ...props.banner,
//   File: {
//     ...props.banner.File,
//     Media: {
//       ...props.banner.File?.Media,
//     },
//     MediaMobile: {
//       ...props.banner.File?.MediaMobile,
//     },
//   },
// })

const banner = computed(() => {
  const banner = props.banner

  if (banner?.File?.Media?.mime?.includes('video')) {
    const match = /(?<width>\d+)[Xx×](?<height>\d+)/.exec(banner?.File?.Media?.name)
    if (match?.groups?.width && match?.groups?.height) {
      banner.File.Media.width = parseInt(match.groups.width)
      banner.File.Media.height = parseInt(match.groups.height)
    }
  }

  if (!banner?.File?.Media?.width || !banner?.File?.Media?.height) {
    banner.File.Media.width = 1600
    banner.File.Media.height = 900
  }

  banner.File.Media.ratio = banner.File.Media.width / banner.File.Media.height

  if (banner?.File?.MediaMobile?.mime?.includes('video')) {
    const match = /(?<width>\d+)[Xx×](?<height>\d+)/.exec(banner?.File?.MediaMobile?.name)
    if (match?.groups?.width && match?.groups?.height) {
      banner.File.MediaMobile.width = parseInt(match.groups.width)
      banner.File.MediaMobile.height = parseInt(match.groups.height)
    }
  }

  if (banner?.File?.MediaMobile) {
    if (!banner?.File?.MediaMobile?.width || !banner?.File?.MediaMobile?.height) {
      banner.File.MediaMobile.width = 900
      banner.File.MediaMobile.height = 1600
    }

    banner.File.MediaMobile.ratio = banner.File.MediaMobile.width / banner.File.MediaMobile.height
  }

  return banner
})

const hasOverlay = computed(() => {
  const hasContent = banner.value?.Title || banner.value?.Description
  const contentAboveImage = ['topleft', 'top', 'topright', 'left', 'center', 'right', 'bottomleft', 'bottom', 'bottomright'].includes(banner.value?.ContentPosition)
  return hasContent && contentAboveImage
})

const widescreenImage = computed(() => {
  const columns = Math.min(props.bannerCount, 3)
  const maxWidth = Math.round((props.fullWidth ? 2560 : 1324) / columns)
  return useXcdnImage(banner.value?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const desktopImage = computed(() => {
  const columns = Math.min(props.bannerCount, 3)
  const maxWidth = Math.round(1280 / columns)
  return useXcdnImage(banner.value?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const tabletImage = computed(() => {
  const columns = Math.min(props.bannerCount, 2)
  const maxWidth = Math.round(1024 / columns)
  return useXcdnImage(banner.value?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const phabletImage = computed(() => {
  const columns = Math.min(props.bannerCount, 2)
  const maxWidth = Math.round(768 / columns)
  return useXcdnImage(banner.value?.File?.MediaMobile?.url || banner.value?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const mobileImage = computed(() => {
  const columns = Math.min(props.bannerCount, 2)
  const maxWidth = Math.round(640 / columns)
  return useXcdnImage(banner.value?.File?.MediaMobile?.url || banner.value?.File?.Media?.url, 'f=rs:fit:' + maxWidth)
})

const isExternal = computed(() => !!banner.value?.File?.Link?.includes('http'))

const hasButton = computed(() => !!(banner.value?.ButtonText || banner.value?.ButtonType === 'icon_only'))

const getStyle = computed(() => {
  return {
    '--mobile-ratio': banner.value.File?.MediaMobile?.ratio || banner.value.File?.Media?.ratio,
    '--desktop-ratio': banner.value.File?.Media?.ratio,
  }
})
</script>

<template>
  <article
    :class="[
      'banner',
      `banner--${banner?.ContentPosition}`,
      {
        'banner--overlay': hasOverlay,
        'banner--external': isExternal,
        'banner--has-button': hasButton,
      },
    ]"
    :style="getStyle"
  >
    <Head
      v-if="!lazy && banner?.File?.Media?.url && banner?.File?.Media?.mime?.includes('image')"
      render-priority="10"
    >
      <Link
        key="banner-widescreen"
        rel="preload"
        as="image"
        :href="widescreenImage"
        fetchpriority="high"
        media="(min-width: 1280px)"
      />
      <Link
        key="banner-desktop"
        rel="preload"
        as="image"
        :href="desktopImage"
        fetchpriority="high"
        media="(min-width: 1024px)"
      />
      <Link
        key="banner-tablet"
        rel="preload"
        as="image"
        :href="tabletImage"
        fetchpriority="high"
        media="(min-width: 768px)"
      />
      <Link
        key="banner-phablet"
        rel="preload"
        as="image"
        :href="phabletImage"
        fetchpriority="high"
        media="(min-width: 640px)"
      />
      <Link
        key="banner-mobile"
        rel="preload"
        as="image"
        :href="mobileImage"
        fetchpriority="high"
        media="(max-width: 639px)"
      />
    </Head>
    <Head
      v-if="!lazy && banner?.File?.Media?.url && banner?.File?.Media?.mime?.includes('video')"
      render-priority="10"
    >
      <Link
        rel="preload"
        as="video"
        :href="banner?.File?.Media?.url"
        fetchpriority="high"
        media="(min-width: 640px)"
      />
      <Link
        rel="preload"
        as="video"
        :href="banner?.File?.MediaMobile?.url"
        fetchpriority="high"
        media="(max-width: 639px)"
      />
    </Head>

    <div class="banner__wrapper">
      <AppLink
        v-if="banner?.File?.Link"
        :to="banner.File.Link"
        class="banner__link"
      >
        <span class="banner__link-text">{{ banner?.Title || banner?.ButtonText || banner.File.Link }}</span>
      </AppLink>
      <div class="banner__container">
        <div class="banner__content">
          <UiHeading
            v-if="banner?.Title"
            class="banner__heading"
          >
            {{ banner.Title }}
          </UiHeading>

          <p
            v-if="banner?.Description"
            class="banner__description"
          >
            {{ banner.Description }}
          </p>

          <div
            v-if="hasButton"
            class="banner__button"
            :class="`banner__button--${banner?.ButtonType}`"
          >
            <span>{{ banner?.ButtonText }}</span>
            <NuxtIcon name="icon:arrow" />
          </div>
        </div>
      </div>

      <div class="banner__visual">
        <picture
          v-if="banner?.File?.Media?.url && banner?.File?.Media?.mime?.includes('image')"
          class="banner__picture"
        >
          <source
            :srcset="widescreenImage"
            media="(min-width: 1280px)"
          >
          <source
            :srcset="desktopImage"
            media="(min-width: 1024px)"
          >
          <source
            :srcset="tabletImage"
            media="(min-width: 768px)"
          >
          <source
            :srcset="phabletImage"
            media="(min-width: 640px)"
          >
          <source
            :srcset="mobileImage"
          >
          <img
            class="banner__image"
            :src="widescreenImage"
            :alt="banner?.File?.Media?.alternativeText || banner?.Name || ''"
            :width="banner?.File?.Media?.width"
            :height="banner?.File?.Media?.height"
            :loading="lazy ? 'lazy' : 'eager'"
            :fetchpriority="lazy ? 'low' : 'high'"
            :decoding="lazy ? 'async' : 'sync'"
          >
        </picture>
        <div
          v-else-if="banner?.File?.Media?.url && banner?.File?.Media?.mime?.includes('video')"
          class="banner__video"
        >
          <UiVideo
            v-if="banner?.File?.Media?.url"
            :class="{ 'video--tablet-plus': banner?.File?.Media?.url && banner?.File?.MediaMobile?.url }"
            :src="banner?.File?.Media?.url"
            :alt="banner?.File?.Media?.alternativeText || banner?.Name"
            :width="banner?.File?.Media?.width"
            :height="banner?.File?.Media?.height"
            :loading="lazy ? 'lazy' : 'eager'"
            :fetchpriority="lazy ? 'auto' : 'high'"
            :controls="false"
            autoplay
            muted
            loop
          />
          <UiVideo
            v-if="banner?.File?.MediaMobile?.url && banner?.File?.MediaMobile?.mime?.includes('video')"
            class="video--mobile-phablet"
            :src="banner?.File?.MediaMobile?.url"
            :alt="banner?.File?.MediaMobile?.alternativeText || banner?.Name"
            :width="banner?.File?.MediaMobile?.width"
            :height="banner?.File?.MediaMobile?.height"
            :loading="lazy ? 'lazy' : 'eager'"
            :fetchpriority="lazy ? 'auto' : 'high'"
            :controls="false"
            autoplay
            muted
            loop
          />
        </div>
      </div>

      <StrapiSticker
        v-if="banner?.Sticker"
        :sticker="banner.Sticker"
        class="banner__sticker"
      />
    </div>
  </article>

  <!-- <pre>getStyle: {{ getStyle }}</pre> -->
  <!-- <pre>banner?.File: {{ banner?.File }}</pre> -->
</template>

<style>
@import url("~layers/components/Strapi/Banner.css");
</style>
